/** @graphql agg **/
import { useCallback, useEffect, useRef, useState } from 'react';

import { graphql } from 'react-relay';

import { ConfluencePageAri } from '@atlassian/ari';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useSmartLinkContext } from '@atlaskit/link-provider/context';

import { expVal } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { extractIncomingLinkAris } from '../utils/extractIncomingLinkAris.ts';
import { resolveIncomingLinks } from '../utils/resolveIncomingLinks.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type {
	useIncomingLinksQuery$data,
	useIncomingLinksQuery$variables,
} from './__generated__/useIncomingLinksQuery.graphql.ts';

const ANALYTICS_SOURCE = 'connectionsBylineMenu';

// Classify by the error's contents rather than the code path: an Apollo error
// with only a networkError is transport-level; one carrying graphQLErrors is a
// query/data failure. Anything unrecognised is treated as a network error.
const classifyError = (err: ApolloErrorInterop): 'network' | 'graphql' =>
	!err.networkError && err.graphQLErrors?.length ? 'graphql' : 'network';

// Fetch all incoming links in a single page; 1000 is the AGG cypherQueryV2 max.
const MAX_INCOMING_LINKS = 1000;

// `(entity)-[:content_entity_links_entity]->(page)`: any-product entity pointing
// at this page. Kept as a constant until confirmed against real TWG data.
const LINKS_RELATIONSHIP = 'content_entity_links_entity';

// Byline only knows the content id, so assume the dominant `page` type; other
// types match nothing and the section stays empty.
const buildConfluencePageAri = (cloudId: string, contentId: string): string =>
	ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString();

// AGG requires an `X-Query-Context` header naming the site (or workspace) ARI for
// `graphStore_*` fields, otherwise the gateway rejects the request with
// "Query context must not be null". The site ARI is derived from the cloudId.
const buildQueryContext = (cloudId: string): string => `ari:cloud:platform::site/${cloudId}`;

const buildIncomingLinksCypher = (pageAri: string): string =>
	[
		`MATCH (entity)-[:${LINKS_RELATIONSHIP}]->(page {ari: "${pageAri}"})`,
		`RETURN entity AS c`,
		`ORDER BY entity.lastUpdated DESC`,
	].join('\n');

// Fetch only the ARI of each incoming entity; display metadata is hydrated
// generically per-ARI via ORS (see `resolveIncomingLinks`), which avoids a
// per-product `... on <Type>` fragment for every product in the graph.
const IncomingLinksQuery = graphql`
	query useIncomingLinksQuery($cypherQuery: String!, $first: Int) {
		graphStore_cypherQueryV2(query: $cypherQuery, first: $first)
			@optIn(to: "GraphStoreCypherQueryV2") {
			edges {
				node {
					columns {
						key
						value {
							... on GraphStoreCypherQueryV2AriNode {
								id
							}
						}
					}
				}
			}
		}
	}
`;

export type UseIncomingLinksResult = {
	/** Flat, recency-ordered list of incoming links. Not grouped by product. */
	links: EnhancedSmartLink[];
	loading: boolean;
	error: ApolloErrorInterop | undefined;
};

/**
 * Fetches cross-product incoming links to a Confluence page (Jira issues,
 * Townsquare goals, other pages, etc. that reference it) from the Teamwork
 * Graph via the experimental AGG `graphStore_cypherQueryV2` field, then hydrates
 * each entity ARI into an `EnhancedSmartLink` via ORS. Returns a flat list.
 *
 * Must be rendered under a SmartCard provider (`useSmartLinkContext`).
 */
export const useIncomingLinks = (contentId: string | undefined): UseIncomingLinksResult => {
	const { cloudId } = useSessionData();
	const { connections } = useSmartLinkContext();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredRef = useRef(false);

	const skip =
		!expVal('1p_3p_connections_byline_twg', 'isEnabled', false) || !contentId || !cloudId;

	const [links, setLinks] = useState<EnhancedSmartLink[]>([]);
	const [resolving, setResolving] = useState(false);

	const reportError = useCallback(
		(err: ApolloErrorInterop) => {
			getMonitoringClient().submitError(err, { attribution: Attribution.INTEGRATIONS });
			markErrorAsHandled(err);
			fireOperationalAnalytics(createAnalyticsEvent({}), 'incomingLinks failed', {
				source: ANALYTICS_SOURCE,
				errorType: classifyError(err),
			});
		},
		[createAnalyticsEvent],
	);

	const {
		data,
		loading: queryLoading,
		error,
	} = useQueryInterop<useIncomingLinksQuery$data, useIncomingLinksQuery$variables>(
		IncomingLinksQuery,
		{
			variables: {
				cypherQuery: skip
					? ''
					: buildIncomingLinksCypher(buildConfluencePageAri(cloudId, contentId)),
				first: MAX_INCOMING_LINKS,
			},
			skip,
			interopApolloAggClient: true,
			context: skip ? undefined : { headers: { 'X-Query-Context': buildQueryContext(cloudId) } },
			onError: reportError,
		},
	);

	// Once the query settles without error, hydrate the returned ARIs into
	// EnhancedSmartLinks via ORS and fire the succeeded event once with the
	// resolved count. Error reporting stays in `onError` (both transport and
	// partial GraphQL errors), so the error case is skipped here. The `cancelled`
	// guard prevents state updates if the async ORS resolution outlives the mount.
	useEffect(() => {
		if (skip || queryLoading || error || !data) {
			return;
		}

		let cancelled = false;
		setResolving(true);

		resolveIncomingLinks(extractIncomingLinkAris(data), connections.client)
			.then((resolved) => {
				if (cancelled) {
					return;
				}
				setLinks(resolved);
				setResolving(false);
				if (!hasFiredRef.current) {
					hasFiredRef.current = true;
					fireOperationalAnalytics(createAnalyticsEvent({}), 'incomingLinks succeeded', {
						source: ANALYTICS_SOURCE,
						count: resolved.length,
					});
				}
			})
			.catch(() => {
				// resolveIncomingLinks swallows and reports per-batch failures itself;
				// this guards against an unexpected rejection leaving `resolving` stuck.
				if (!cancelled) {
					setResolving(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [skip, queryLoading, error, data, connections.client, createAnalyticsEvent]);

	return { links, loading: queryLoading || resolving, error };
};
